import React from 'react';
import { Button } from '@/components/ui/button';
import { ExtractedContent } from '@/config/richContentConfig';
import { SchedulingWidget } from './content/SchedulingWidget';
import { IframeContent } from './content/IframeContent';
import { LinkPreview } from './content/LinkPreview';
import { InlineLink } from './content/InlineLink';
import { useChatContext } from '@/hooks/useChatContext';
import { parseSchedulingConfig } from '@/types/scheduling.types';

interface RichContentRendererProps {
  content: ExtractedContent;
  onButtonClick?: (text: string, attributes?: Record<string, string>) => void;
  brandColor?: string;
}

/**
 * Renders the appropriate component based on the content type
 */
export const RichContentRenderer: React.FC<RichContentRendererProps> = ({
  content,
  onButtonClick,
  brandColor = "#060520"
}) => {
  const chatContext = useChatContext();

  // Switch based on content type
  switch (content.type) {
    case 'BUTTON':
      // Check if it's a link button with target attribute
      if (content.attributes?.target === 'blank' || content.attributes?.target === 'link') {
        return (
          <Button
            variant="outline"
            size="sm"
            onClick={() => window.open(content.attributes?.url, '_blank')}
            className="mt-2 text-white hover:bg-opacity-90"
            style={{
              backgroundColor: brandColor,
              borderColor: brandColor
            }}
          >
            {content.content}
          </Button>
        );
      }

      // Regular button
      return (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onButtonClick?.(content.content, content.attributes)}
          className="mt-2 border border-gray-200 bg-white text-gray-700 hover:bg-gray-100"
          style={{
            borderColor: `${brandColor}50`,
            color: brandColor
          }}
        >
          {content.content}
        </Button>
      );

    case 'SCHEDULING':
      console.log('Rendering scheduling widget with content:', content.content);

      // Parse scheduling configuration safely
      const schedulingConfig = parseSchedulingConfig(chatContext?.agent?.skills_scheduling);

      // Check if agent has scheduling enabled before rendering the widget
      if (!schedulingConfig?.enabled) {
        console.warn('Attempted to render scheduling widget for agent without scheduling enabled');
        return (
          <div className="w-full p-3 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-md my-2 text-sm">
            Scheduling is not available for this agent. The administrator needs to enable this feature.
          </div>
        );
      }

      try {
        return (
          <SchedulingWidget
            buttonText={content.content}
            provider="calendly"
          />
        );
      } catch (error) {
        console.error('Error rendering scheduling widget:', error);
        return (
          <div className="w-full p-3 bg-red-50 text-red-600 border border-red-200 rounded-md my-2 text-sm">
            Unable to display scheduling widget. Please try again later.
          </div>
        );
      }

    case 'CALENDLY':
      console.log('Rendering calendly widget with content:', content.content);

      // Parse scheduling configuration safely
      const calendlySchedulingConfig = parseSchedulingConfig(chatContext?.agent?.skills_scheduling);

      // Check if agent has scheduling enabled before rendering the widget
      if (!calendlySchedulingConfig?.enabled) {
        console.warn('Attempted to render calendly widget for agent without scheduling enabled');
        return (
          <div className="w-full p-3 bg-yellow-50 text-yellow-700 border border-yellow-200 rounded-md my-2 text-sm">
            Scheduling is not available for this agent. The administrator needs to enable this feature.
          </div>
        );
      }

      try {
        return (
          <SchedulingWidget
            buttonText={content.content}
            provider="calendly"
          />
        );
      } catch (error) {
        console.error('Error rendering calendly widget:', error);
        return (
          <div className="w-full p-3 bg-red-50 text-red-600 border border-red-200 rounded-md my-2 text-sm">
            Unable to display calendly widget. Please try again later.
          </div>
        );
      }

    case 'IFRAME':
      return (
        <IframeContent
          src={content.attributes?.src || ''}
          height={content.attributes?.height ? parseInt(content.attributes.height) : 500}
          width={content.attributes?.width || '100%'}
        />
      );

    case 'LINK':
      return (
        <LinkPreview
          url={content.attributes?.url || ''}
          title={content.attributes?.title || content.content}
          brandColor={brandColor}
        />
      );

    case 'HYPERLINK':
      return (
        <InlineLink
          url={content.attributes?.url || ''}
          text={content.content}
          brandColor={brandColor}
        />
      );

    // We don't handle FOLLOWUP here since it's managed separately
    default:
      // Return null if we don't know how to render this content type
      return null;
  }
}; 